/**
 * Fleet summary — `GET /admin/fleet-summary`.
 *
 * The Strategies page used to fetch today's trades (`limit: 500`) and sum them in
 * the browser, once per card. Past 500 trades in a day every figure quietly
 * under-reported, and the work was O(deployments × trades) on a 5s poll.
 *
 * The roll-up is now a database aggregate over the FULL trade set. Two invariants
 * this module exists to preserve:
 *
 *   • `totals` always covers every live deployment, never the card slice.
 *   • `cards` is BOUNDED and `cards_total` says what it was drawn from, so the
 *     UI can show "8 of 47" rather than implying it showed everything.
 */
import { z } from 'zod';

import type { ApiResult } from './client';
import { get } from './client';

export const fleetCurrencyTotalSchema = z.object({
  currency: z.string(),
  realized_pnl: z.number(),
  accounts: z.number(),
});

export const fleetCardSchema = z.object({
  deployment_id: z.string(),
  subscription_id: z.string().nullable().optional(),
  broker_account_id: z.string().nullable().optional(),
  account_name: z.string().nullable().optional(),
  strategy_id: z.string(),
  /** Null when no account is attached — the UI dashes it, never assumes. */
  currency: z.string().nullable().optional(),
  status: z.string(),
  risk_halted: z.boolean(),
  realized_pnl: z.number(),
  charges: z.number(),
  trades: z.number(),
  closed_trades: z.number(),
  open_trades: z.number(),
  wins: z.number(),
  losses: z.number(),
  curve: z.array(z.number()),
  open_symbol: z.string().nullable().optional(),
  open_qty: z.number().nullable().optional(),
  open_side: z.string().nullable().optional(),
  scheduler_state: z.string().nullable().optional(),
  universe: z.string().nullable().optional(),
  /** Why this card ranked in: 'halted' | 'open' | 'pnl'. */
  reason: z.string(),
});

export const fleetSummarySchema = z.object({
  since: z.string(),
  totals: z.array(fleetCurrencyTotalSchema),
  running: z.number(),
  failed: z.number(),
  stopped: z.number(),
  halted: z.number(),
  cards: z.array(fleetCardSchema),
  cards_total: z.number(),
});

export type FleetCurrencyTotal = z.infer<typeof fleetCurrencyTotalSchema>;
export type FleetCard = z.infer<typeof fleetCardSchema>;
export type FleetSummary = z.infer<typeof fleetSummarySchema>;

export const getFleetSummary = (
  query: { since: string; limit?: number },
  signal?: AbortSignal,
): Promise<ApiResult<FleetSummary>> =>
  get('/admin/fleet-summary', fleetSummarySchema, {
    signal,
    query: { since: query.since, limit: query.limit },
  }) as Promise<ApiResult<FleetSummary>>;
